import styled from 'styled-components';
import MyTable, { Column } from '../../components/MyTable';
import { useEffect, useState, useMemo } from 'react';

const today = new Date();
const formattedDate = `${today.getFullYear()}년 ${today.getMonth() + 1}월 ${today.getDate()}일 기준`;

type WeeklyTrendItem = {
  id: string;
  week: string;
  period: string;
  wonCount: number;
  winRate: number;
  winPriceRate: number;
};

const columns: Column<WeeklyTrendItem>[] = [
  {
    key: 'week',
    label: '기간',
    align: 'left',
  },
  { key: 'wonCount', label: '낙찰건수', align: 'right' },
  {
    key: 'winRate',
    label: '낙찰률',
    align: 'right',
    render: (item) => `${item.winRate.toFixed(1)}%`,
  },
  {
    key: 'winPriceRate',
    label: '낙찰가율',
    align: 'right',
    render: (item) => `${item.winPriceRate.toFixed(1)}%`,
  },
];

const transformBidPriceRate = (data: any): WeeklyTrendItem[] => {
  if (!data) return [];

  const mapPeriod: Record<string, string> = {
    mon_3: '3개월 평균',
    mon_6: '6개월 평균',
    mon_12: '1년간 평균',
  };

  return Object.entries(data).map(([key, values], index) => {
    const [wonCount, winRate, winPriceRate] = values as (
      | string
      | number
      | null
    )[];

    return {
      id: String(index + 1),
      week: mapPeriod[key] ?? key,
      period: '-',
      wonCount: Number(wonCount) || 0,
      winRate: Number(winRate) || 0,
      winPriceRate: Number(winPriceRate) || 0,
    };
  });
};
interface RegionBidRateChartProps {
  data?: {
    bidPriceRate?: {
      sido?: Record<string, (string | number | null)[]>;
      gugun?: Record<string, (string | number | null)[]>;
      dong?: Record<string, (string | number | null)[]>;
    };
    land?: {
      ctp_nm?: string;
      sig_nm?: string;
      emd_nm?: string;
    };
    category?: string;
  };
  isPremium?: boolean;
}

const RegionBidRateChart = ({
  data,
  isPremium = false,
}: RegionBidRateChartProps) => {
  const [tableData, setTableData] = useState<WeeklyTrendItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // 지역 옵션
  const options = useMemo(() => {
    const { ctp_nm, sig_nm, emd_nm } = data?.land ?? {};
    return [
      { key: 'sido', label: `${ctp_nm ?? '시도'} 전체` },
      { key: 'gugun', label: `${sig_nm ?? '구군'} 전체` },
      { key: 'dong', label: `${emd_nm ?? '동'}` },
    ];
  }, [data]);

  const handleDataChange = (key: string) => {
    const rateData = data?.bidPriceRate;

    if (!rateData) return;

    setIsLoading(true);

    switch (key) {
      case 'sido':
        setTableData(transformBidPriceRate(rateData.sido));
        break;
      case 'gugun':
        setTableData(transformBidPriceRate(rateData.gugun));
        break;
      case 'dong':
        setTableData(transformBidPriceRate(rateData.dong));
        break;
      default:
        setTableData([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!data) return;

    setIsLoading(true);
    setTimeout(() => {
      if (data?.bidPriceRate?.sido) {
        setTableData(transformBidPriceRate(data.bidPriceRate.sido));
      }
      setIsLoading(false);
    }, 300);
  }, [data, isPremium]);

  return (
    <Wrap>
      <MyTable
        title="지역별/기간별 평균 낙찰가율"
        description="<p>해당 용도의 지역별, 기간별</p> <p>평균 낙찰률, 낙찰가율, 낙찰 건수</p>"
        selectionOptions={[
          formattedDate,
          `${data?.land?.ctp_nm ?? '시도'} ${data?.land?.sig_nm ?? '구군'} ${data?.land?.emd_nm ?? '동'}`,
          data?.category,
        ]}
        columns={columns}
        data={tableData}
        extraTop={options}
        onDataChange={handleDataChange}
        loading={isLoading}
        isPremium={isPremium}
      />
    </Wrap>
  );
};

export default RegionBidRateChart;

const Wrap = styled.div`
  width: 100%;
  overflow-x: hidden;
`;
